import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { FileDown, Loader2, Settings, Eye, QrCode, Type, Tag } from 'lucide-react';

const ShopExport = () => {
  const { shopId } = useParams();
  const { api } = useAuth();
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [shop, setShop] = useState(null);
  const [categories, setCategories] = useState([]);
  const [productCounts, setProductCounts] = useState({});
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showDescriptions, setShowDescriptions] = useState(true);
  const [showPrices, setShowPrices] = useState(true);
  const [showBackPage, setShowBackPage] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [shopRes, catsRes, prodsRes] = await Promise.all([
          api.get(`/shops/${shopId}`),
          api.get(`/shops/${shopId}/categories`),
          api.get(`/shops/${shopId}/products`)
        ]);
        setShop(shopRes.data);
        setCategories(catsRes.data);
        const counts = {};
        prodsRes.data.forEach(p => {
          counts[p.categoryId] = (counts[p.categoryId] || 0) + 1;
        });
        setProductCounts(counts);
        setSelectedCategories(catsRes.data.map(c => c.id));
      } catch (e) {
        toast.error('Erreur lors du chargement');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [api, shopId]);

  const toggleCategory = (catId) => {
    setSelectedCategories(prev =>
      prev.includes(catId) ? prev.filter(id => id !== catId) : [...prev, catId]
    );
  };

  const toggleAllCategories = () => {
    if (selectedCategories.length === categories.length) {
      setSelectedCategories([]);
    } else {
      setSelectedCategories(categories.map(c => c.id));
    }
  };

  const totalProducts = selectedCategories.reduce((sum, catId) => sum + (productCounts[catId] || 0), 0);

  const handleExport = async () => {
    if (selectedCategories.length === 0) {
      toast.error('Selectionnez au moins une categorie');
      return;
    }
    setGenerating(true);
    try {
      const response = await api.post(`/shops/${shopId}/catalogue/pdf`, {
        categoryIds: selectedCategories,
        showDescriptions,
        showPrices,
        showBackPage
      });
      const { pdf, filename } = response.data;
      const byteString = atob(pdf.split(',')[1]);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Catalogue PDF telecharge !');
    } catch (error) {
      toast.error('Erreur lors de la generation du PDF');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl" data-testid="shop-export">
      <div>
        <h1 className="text-3xl font-bold font-['Manrope']">Export PDF</h1>
        <p className="text-muted-foreground mt-1">Personnalisez et generez votre catalogue</p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Type className="w-4 h-4" />
            Message de couverture
          </CardTitle>
        </CardHeader>
        <CardContent>
          {shop?.slogan ? (
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <p className="text-sm italic" data-testid="slogan-preview">&ldquo;{shop.slogan}&rdquo;</p>
              <Link to={`/app/shops/${shopId}/settings`} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="edit-slogan-link">
                <Settings className="w-3 h-3" /> Modifier
              </Link>
            </div>
          ) : (
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground" data-testid="no-slogan-text">Aucun slogan defini</p>
              <Link to={`/app/shops/${shopId}/settings`} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="add-slogan-link">
                <Settings className="w-3 h-3" /> Ajouter
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Tag className="w-4 h-4" />
              Categories a inclure
            </CardTitle>
            <button
              onClick={toggleAllCategories}
              className="text-xs text-primary hover:underline"
              data-testid="toggle-all-categories"
            >
              {selectedCategories.length === categories.length ? 'Tout deselectionner' : 'Tout selectionner'}
            </button>
          </div>
          <CardDescription data-testid="category-summary">{selectedCategories.length}/{categories.length} categories · {totalProducts} produits</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {categories.map(cat => (
              <label
                key={cat.id}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedCategories.includes(cat.id)
                    ? 'border-primary/50 bg-primary/5'
                    : 'border-border hover:bg-muted/50'
                }`}
                data-testid={`category-toggle-${cat.id}`}
              >
                <Checkbox
                  checked={selectedCategories.includes(cat.id)}
                  onCheckedChange={() => toggleCategory(cat.id)}
                />
                <span className="flex-1 text-sm font-medium">{cat.name}</span>
                <span className="text-xs text-muted-foreground">{productCounts[cat.id] || 0} produits</span>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Eye className="w-4 h-4" />
            Options d'affichage
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Descriptions produits</Label>
              <p className="text-xs text-muted-foreground">Texte descriptif sous chaque produit</p>
            </div>
            <Switch checked={showDescriptions} onCheckedChange={setShowDescriptions} data-testid="toggle-descriptions" />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Prix</Label>
              <p className="text-xs text-muted-foreground">Prix et promotions des produits</p>
            </div>
            <Switch checked={showPrices} onCheckedChange={setShowPrices} data-testid="toggle-prices" />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm flex items-center gap-1.5">
                <QrCode className="w-3.5 h-3.5" /> Page QR code
              </Label>
              <p className="text-xs text-muted-foreground">Derniere page avec QR code et coordonnees</p>
            </div>
            <Switch checked={showBackPage} onCheckedChange={setShowBackPage} data-testid="toggle-back-page" />
          </div>
        </CardContent>
      </Card>

      <Button
        onClick={handleExport}
        disabled={generating || selectedCategories.length === 0}
        className="w-full rounded-full"
        size="lg"
        data-testid="export-pdf-btn"
      >
        {generating ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Generation en cours...
          </>
        ) : (
          <>
            <FileDown className="w-4 h-4 mr-2" />
            Telecharger le catalogue PDF ({totalProducts} produits)
          </>
        )}
      </Button>
    </div>
  );
};

export default ShopExport;
